import { useEffect, useMemo, useState } from "react";
import { Consent, hasAnswered, writeConsent, readConsent } from "../lib/consent";
import { initAnalyticsFromStoredConsent, applyAnalyticsConsent } from "../lib/analytics";

// measurementId wird nicht mehr benötigt -> kannst du entfernen, wenn du willst
export default function CookieConsent(/* { measurementId }: { measurementId?: string } */) {
  const [showBanner, setShowBanner] = useState(!hasAnswered());
  const [openPrefs, setOpenPrefs] = useState(false);

  // ⬇️ Initialwert so typisieren, dass necessary ein Literal "true" bleibt
  const initial = useMemo<Consent>(
    () => readConsent() ?? ({ necessary: true as const, analytics: false } as const),
    []
  );
  const [analyticsOn, setAnalyticsOn] = useState<boolean>(initial.analytics);

  // Von überall öffnen (Footer-Button ruft openConsentSettings() auf)
  useEffect(() => {
    // kleine TS-Verbesserung: Event-Parameter beachten
    const onOpen = (_e: Event) => setOpenPrefs(true);
    window.addEventListener("cookie-consent:open", onOpen);
    return () => window.removeEventListener("cookie-consent:open", onOpen);
  }, []);

  // Beim Mount ggf. GA aus bestehendem Consent starten
  useEffect(() => {
    initAnalyticsFromStoredConsent();
  }, []);

  function acceptAll() {
    // ⬇️ Objekt als Consent typisieren
    const c: Consent = { necessary: true, analytics: true, timestamp: new Date().toISOString() };
    writeConsent(c);
    applyAnalyticsConsent(true);
    setShowBanner(false);
    setOpenPrefs(false);
  }

  function rejectAll() {
    // ⬇️ Objekt als Consent typisieren
    const c: Consent = { necessary: true, analytics: false, timestamp: new Date().toISOString() };
    writeConsent(c);
    applyAnalyticsConsent(false);
    setShowBanner(false);
    setOpenPrefs(false);
  }

  function savePrefs() {
    // ⬇️ Objekt als Consent typisieren
    const c: Consent = { necessary: true, analytics: analyticsOn, timestamp: new Date().toISOString() };
    writeConsent(c);
    applyAnalyticsConsent(analyticsOn);
    setShowBanner(false);
    setOpenPrefs(false);
  }

  return (
    <>
      {/* Banner (nur solange keine Antwort vorliegt) */}
      {showBanner && (
        <div className="fixed inset-x-0  z-50 p-8">
          <div className="mx-auto max-w-3xl rounded-xl border border-amber-500/30 bg-[#161b27] shadow-lg p-4 sm:p-5">
            <p className="text-sm text-slate-100"><strong>Volle Kontrolle über deine Daten-Krümel!</strong><br></br><br></br></p>

            <p className="text-sm text-slate-300">

              Wir setzen nur die Cookies, die für die Funktion der Seite unerlässlich sind. Optional hilfst du uns riesig, wenn du die anonyme Nutzungsanalyse (GA4) aktivierst. Du hast das Sagen und kannst deine Meinung jederzeit ändern.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={acceptAll} className="rounded-lg bg-sky-500 text-white px-3 py-2 text-sm hover:bg-sky-600 transition-colors">
                Alle akzeptieren
              </button>
              <button onClick={rejectAll} className="rounded-lg border border-[#2a3347] text-slate-300 px-3 py-2 text-sm hover:bg-[#1e2639] transition-colors">
                Alle ablehnen
              </button>
              <button onClick={() => setOpenPrefs(true)} className="text-sm text-sky-400 hover:underline">
                Einstellungen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Einstellungs-Dialog */}
      {openPrefs && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-lg rounded-2xl bg-[#161b27] border border-[#2a3347] shadow-xl p-5">
            <h2 className="text-base font-semibold text-slate-100">Cookie- & Tracking-Einstellungen</h2>
            <p className="mt-1 text-sm text-slate-300">
              Du kannst optional anonymisierte Nutzungsanalyse aktivieren. Technisch notwendige Cookies sind immer aktiv.
            </p>

            <div className="mt-4 space-y-3">
              <div className="rounded-lg border border-[#2a3347] p-3 bg-[#1e2639]">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-slate-100">Notwendig</div>
                    <div className="text-sm text-slate-400">
                      Session, Sicherheit, PWA-Cache. Für Grundfunktionen erforderlich.
                    </div>
                  </div>
                  <span className="text-xs text-slate-500">immer aktiv</span>
                </div>
              </div>

              <label className="flex items-start justify-between rounded-lg border border-[#2a3347] p-3 hover:bg-[#1e2639] transition-colors cursor-pointer">
                <div>
                  <div className="font-medium text-slate-100">Analytics (Google Analytics 4)</div>
                  <div className="text-sm text-slate-400">
                    Anonymisierte Seitenaufrufe & Klicks zur Produktverbesserung.
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-sky-500"
                  checked={analyticsOn}
                  onChange={(e) => setAnalyticsOn(e.target.checked)}
                />
              </label>
            </div>

            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <button onClick={rejectAll} className="rounded-lg border border-[#2a3347] text-slate-300 px-3 py-2 text-sm hover:bg-[#1e2639] transition-colors">
                Alle ablehnen
              </button>
              <button onClick={savePrefs} className="rounded-lg bg-sky-500 text-white px-3 py-2 text-sm hover:bg-sky-600 transition-colors">
                Auswahl speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
